import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useApplicationSchema } from "hooks/useApplicationSchema";
import StepRenderer from "components/Registration/StepRenderer";
import DocumentsStepRenderer from "components/Registration/DocumentsStepRenderer";
import Skeleton from "components/Skeleton";
import { applicationPercentErrors, describePercentErrors } from "utilities/percentBounds";
import {
  Save,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  X,
} from "lucide-react";

/**
 * Editable form for a company's draft application.
 *
 * Mounts when the Registration tab is toggled into edit mode. Loads the
 * canonical schema, renders each schema step using the existing field
 * components, and persists changes to `PUT /applications/:id`. A "Submit
 * for review" button is enabled once all required fields are filled
 * (verified client-side against the completeness endpoint; the server
 * re-validates on submit).
 */
export default function ApplicationEditor({
  companyId,
  application,
  onExit,
  onSaved,
}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const { schema, isLoading: schemaLoading } = useApplicationSchema();

  const [data, setData] = useState(application || null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completeness, setCompleteness] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});
  // Tracks whether `data` has typed edits that haven't been persisted.
  // Used to (1) guard against the prop-sync useEffect silently wiping
  // unsaved work when the parent refetches, (2) warn the user before
  // they navigate away, and (3) auto-save before submit.
  const [isDirty, setIsDirty] = useState(false);

  // Sync local form state from the server-side application.
  // Only resync when the user has NO unsaved edits — otherwise a parent
  // refetch (post-save, impersonation switch, etc.) would silently blow
  // away typed changes.
  useEffect(() => {
    if (!isDirty) {
      setData(application || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application]);

  // Warn the user before they leave the page with unsaved changes.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Modern browsers ignore the custom string but require returnValue set.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Pull completeness on mount and whenever the application changes.
  const fetchCompleteness = async () => {
    if (!effectiveCompanyId || !companyId) return;
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getApplicationCompleteness(companyId),
        auth.user,
        effectiveCompanyId
      );
      setCompleteness(res?.data || null);
    } catch (err) {
      console.error("[ApplicationEditor] Failed to fetch completeness", err);
      setCompleteness(null);
    }
  };

  useEffect(() => {
    fetchCompleteness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, application?.updatedAt]);

  // Group missing fields by step for the banner breakdown.
  const missingByStep = useMemo(() => {
    if (!completeness?.missing) return {};
    const grouped = {};
    for (const entry of completeness.missing) {
      const k = entry.step || "_other";
      if (!grouped[k]) grouped[k] = [];
      grouped[k].push(entry);
    }
    return grouped;
  }, [completeness]);

  const stepTitleFor = (stepKey) => {
    const step = schema?.steps?.find((s) => s.key === stepKey);
    return step?.title || stepKey;
  };

  const handleDataChange = (section, field, value) => {
    setIsDirty(true);
    setData((prev) => {
      const next = prev ? { ...prev } : {};
      if (field === null) {
        // Array section (repeatable — DBAs/owners). Replace wholesale.
        next[section] = value;
      } else {
        next[section] = { ...(prev?.[section] || {}), [field]: value };
      }
      return next;
    });
  };

  // Returns true on success, false on failure. Used both by the Save button
  // and by handleSubmit (to guarantee unsaved edits are persisted before
  // status transition).
  const handleSave = async ({ silent = false } = {}) => {
    if (!data) return false;
    // SOUND-777: never persist a negative or >100 percentage the user CHANGED.
    // A bad value already on record (the `application` prop is the server's
    // copy) warns inline but does not block, mirroring sound-api's
    // enforceRangeOnChangedValues — this path PUTs the whole record.
    const percentErrors = applicationPercentErrors(schema, data, application);
    if (percentErrors.length > 0) {
      toast({
        status: "destructive",
        title: "Fix the percentages first",
        description: describePercentErrors(percentErrors),
      });
      return false;
    }
    setSaving(true);
    try {
      // Strip server-managed fields before sending — the API ignores unknown
      // fields but this matches the pattern used in `hooks/crm/useRegistration.js`.
      const {
        _id,
        documents,
        status,
        submittedAt,
        statusHistory,
        createdAt,
        updatedAt,
        ...payload
      } = data;

      await makeApiRequest(
        "put",
        apiRoutes.updateApplication(companyId),
        auth.user,
        effectiveCompanyId,
        payload
      );

      // Mark clean BEFORE the refetch fires so the prop-sync useEffect can
      // safely overwrite local state with the server's authoritative version.
      setIsDirty(false);

      if (!silent) {
        toast({
          status: "success",
          title: "Draft saved",
          description: "Your changes are stored.",
        });
      }

      if (typeof onSaved === "function") await onSaved();
      fetchCompleteness();
      return true;
    } catch (err) {
      console.error("[ApplicationEditor] Save failed", err);
      const serverError = err.response?.data?.error;
      const details = err.response?.data?.details;
      let description = serverError || "Unknown error";
      if (Array.isArray(details) && details.length > 0) {
        const messages = details
          .slice(0, 3)
          .map((d) => {
            const idx = typeof d.index === "number" ? ` (item ${d.index + 1})` : "";
            return `${d.message}${idx}`;
          });
        if (details.length > 3) messages.push(`(+${details.length - 3} more)`);
        description = messages.join("; ");
      }
      toast({
        status: "destructive",
        title: "Failed to save draft",
        description,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!companyId) return;

    // Persist any unsaved typed edits BEFORE flipping status. The submit
    // endpoint just transitions the status — it doesn't read a request
    // body. Without this guard, partners who edit fields and click Submit
    // without first clicking Save would submit the stale (pre-edit)
    // version of their application.
    if (isDirty) {
      const ok = await handleSave({ silent: true });
      if (!ok) {
        toast({
          status: "destructive",
          title: "Couldn't submit",
          description:
            "Your changes couldn't be saved. Fix any errors above and try again.",
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.submitApplication(companyId),
        auth.user,
        effectiveCompanyId,
        null
      );
      toast({
        status: "success",
        title: "Submitted for review",
        description: "Sound will review the application shortly.",
      });
      if (typeof onSaved === "function") await onSaved();
      onExit?.();
    } catch (err) {
      console.error("[ApplicationEditor] Submit failed", err);
      const serverError = err.response?.data?.error;
      toast({
        status: "destructive",
        title: "Failed to submit",
        description: serverError || "Check that all required fields are filled.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentUpload = async (files, type) => {
    if (!companyId || !files) return;
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("documentType", type);
    try {
      const res = await makeApiRequest(
        "post",
        apiRoutes.uploadApplicationFile(companyId),
        auth.user,
        effectiveCompanyId,
        formData,
        true
      );
      if (res?.status === 201) {
        const { documentType, files: uploadedFiles } = res.data.data;
        setData((prev) => {
          const next = { ...(prev || {}) };
          const docs = Array.isArray(next.documents) ? [...next.documents] : [];
          const idx = docs.findIndex((d) => d.type === documentType);
          if (idx > -1) {
            docs[idx] = {
              ...docs[idx],
              files: [...(docs[idx].files || []), ...uploadedFiles],
            };
          } else {
            docs.push({ type: documentType, files: uploadedFiles });
          }
          next.documents = docs;
          return next;
        });
      }
    } catch (err) {
      console.error("[ApplicationEditor] Upload failed", err);
      toast({
        status: "destructive",
        title: "Upload failed",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const handleDocumentDelete = async (fileKey, type) => {
    if (!companyId || !fileKey || !type) return;
    const url =
      apiRoutes.deleteApplicationFile(companyId) +
      `?documentType=${encodeURIComponent(type)}&key=${encodeURIComponent(fileKey)}`;
    try {
      await makeApiRequest("delete", url, auth.user, effectiveCompanyId);
      setData((prev) => {
        const next = { ...(prev || {}) };
        const docs = Array.isArray(next.documents) ? [...next.documents] : [];
        const idx = docs.findIndex((d) => d.type === type);
        if (idx > -1) {
          const remaining = (docs[idx].files || []).filter(
            (f) => f.key !== fileKey
          );
          if (remaining.length === 0) {
            docs.splice(idx, 1);
          } else {
            docs[idx] = { ...docs[idx], files: remaining };
          }
          next.documents = docs;
        }
        return next;
      });
    } catch (err) {
      console.error("[ApplicationEditor] Delete failed", err);
      toast({
        status: "destructive",
        title: "Delete failed",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const total = completeness?.total || 0;
  const filled = completeness?.filled || 0;
  const percent = total > 0 ? Math.round((filled / total) * 100) : 0;
  const isComplete = total > 0 && filled >= total;

  if (schemaLoading || !schema || !data) {
    return (
      <div className="space-y-6">
        <Skeleton.Rect className="h-48 w-full" />
        <Skeleton.Rect className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Banner: completeness + missing field breakdown */}
      <div className="surface rounded-xl ring-1 ring-border p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`p-2 rounded-lg ${
                isComplete ? "bg-success/20 text-success" : "bg-warning/20 text-warning"
              }`}
            >
              {isComplete ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold">
                {isComplete
                  ? "All required fields are filled"
                  : `${filled} of ${total} required fields filled`}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isComplete
                  ? "Submit for review when you are ready."
                  : `${percent}% complete — keep going.`}
              </p>
            </div>
          </div>
          <button
            className="btn btn-ghost"
            onClick={onExit}
            title="Exit edit mode"
          >
            <X className="h-4 w-4" />
            Exit edit
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-4 w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all ${
              isComplete ? "bg-success" : "bg-brand"
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>

        {/* Per-step breakdown */}
        {!isComplete && Object.keys(missingByStep).length > 0 && (
          <div className="mt-4 border-t border-border pt-4 space-y-2">
            {Object.entries(missingByStep).map(([stepKey, entries]) => {
              const expanded = !!expandedSteps[stepKey];
              return (
                <div
                  key={stepKey}
                  className="rounded-lg border border-border bg-muted/30"
                >
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-3 py-2 text-left"
                    onClick={() =>
                      setExpandedSteps((prev) => ({
                        ...prev,
                        [stepKey]: !prev[stepKey],
                      }))
                    }
                  >
                    <span className="text-sm font-medium text-foreground">
                      {stepTitleFor(stepKey)}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      {entries.length} missing
                      {expanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </span>
                  </button>
                  {expanded && (
                    <ul className="px-3 pb-3 space-y-1">
                      {entries.map((e) => (
                        <li
                          key={`${e.step}.${e.field}`}
                          className="text-xs text-muted-foreground"
                        >
                          • {e.label || e.field}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Schema-driven form — one section per step */}
      {schema.steps.map((step) => (
        <div key={step.key} className="surface rounded-xl ring-1 ring-border p-6">
          <StepRenderer
            stepSchema={step}
            data={data}
            storedData={application}
            onChange={handleDataChange}
          />
        </div>
      ))}

      {/* Documents */}
      {schema.documents?.length > 0 && (
        <div className="surface rounded-xl ring-1 ring-border p-6">
          <DocumentsStepRenderer
            documentSchema={schema.documents}
            data={data}
            handleDocumentUpload={handleDocumentUpload}
            handleDocumentDelete={handleDocumentDelete}
          />
        </div>
      )}

      {/* Action bar */}
      <div className="surface rounded-xl ring-1 ring-border p-5 sticky bottom-4 z-10 flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground flex items-center gap-3">
          {isDirty && (
            <span
              className="inline-flex items-center gap-1.5 text-warning font-medium"
              title="You have unsaved typed changes. Click Save draft (or Submit, which auto-saves) to persist them."
            >
              <span className="h-2 w-2 rounded-full bg-warning" aria-hidden />
              Unsaved changes
            </span>
          )}
          <span>
            {isComplete
              ? "Ready to submit."
              : `${total - filled} required field(s) remaining.`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn btn-secondary"
            onClick={() => handleSave()}
            disabled={saving || submitting || !isDirty}
            title={
              isDirty
                ? "Save your typed changes"
                : "No unsaved changes"
            }
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!isComplete || saving || submitting}
            title={
              !isComplete
                ? "Fill all required fields to enable submission"
                : isDirty
                ? "Save your changes and submit for Sound Commerce review"
                : "Submit this draft for Sound Commerce review"
            }
          >
            <CheckCircle2 className="h-4 w-4" />
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </div>
    </div>
  );
}
